import axios from 'axios';
import { ApiResponse, Pool, PoolUpdate } from '../types/api';
import { getPoolIdByCode, clearPoolCache } from '../utils/poolUtils';
import { API_ORIGIN } from '../config';

// API 설정
const API_URL = API_ORIGIN;

/**
 * API 응답 처리 표준 함수
 */
const handleApiResponse = <T>(response: any): ApiResponse<T> => {
  if (response.data && typeof response.data.success === 'boolean') {
    return {
      success: response.data.success,
      data: response.data.data as T,
      message: response.data.message || (response.data.success ? '요청 성공' : '요청 실패'),
      status: response.status
    };
  } else {
    return {
      success: response.status >= 200 && response.status < 300,
      data: response.data as T,
      message: response.status >= 200 && response.status < 300 ? '요청 성공' : `요청 실패 (상태 ${response.status})`,
      status: response.status
    };
  }
};

/**
 * API 오류 처리 표준 함수
 */
const handleApiError = <T>(error: any): ApiResponse<T> => {
  console.error('API 처리 오류:', error);
  const status = error.response?.status || 500;
  
  let message = '알 수 없는 오류';
  if (error.response?.data?.detail) {
    message = error.response.data.detail;
  } else if (error.response?.data?.message) {
    message = error.response.data.message;
  } else if (error.message) {
    message = error.message;
  }
  
  return {
    success: false,
    data: null,
    message,
    status,
    error: error
  };
};

// 시설 설정 인터페이스
export interface PoolSettings {
  illuminance_threshold?: number;
  temperature_min?: number;
  temperature_max?: number;
  max_capacity_warning?: number;
  emergency_contact?: string;
  auto_alert?: boolean;
  maintenance_interval?: number;
  operating_hours?: string;
}

/**
 * 시설 API 모듈
 * 표준 패턴: URL 접근은 pool_code, 내부 처리는 pool_id 사용
 */
export const poolApi = {
  /**
   * 모든 시설 목록 조회
   */
  getPools: async (): Promise<ApiResponse<Pool[]>> => {
    try {
      console.log('[poolApi] 시설 목록 조회 요청');
      const response = await axios.get('/api/pools');
      console.log('[poolApi] 응답 데이터:', response.data);

      const handled = handleApiResponse<Pool[] | { pools: Pool[] }>(response);
      const raw = handled.data as Pool[] | { pools?: Pool[] } | null;
      if (raw && !Array.isArray(raw) && Array.isArray(raw.pools)) {
        return { ...handled, data: raw.pools };
      }
      return handled as ApiResponse<Pool[]>;
    } catch (error) {
      console.error('[poolApi] 시설 목록 조회 오류:', error);
      return handleApiError<Pool[]>(error);
    }
  },
  
  /**
   * 시설 ID로 시설 정보 조회
   * 내부 처리용 (직접 호출하지 않음)
   */
  getPoolById: async (poolId: string): Promise<ApiResponse<Pool>> => {
    try {
      console.log(`[poolApi] 시설 조회 요청 (ID: ${poolId})`);
      const response = await axios.get(`${API_URL}/api/pools/${poolId}`);
      console.log('[poolApi] 응답 데이터:', response.data);
      
      return handleApiResponse<Pool>(response);
    } catch (error) {
      console.error(`[poolApi] 시설 조회 오류 (ID: ${poolId}):`, error);
      return handleApiError<Pool>(error);
    }
  },
  
  /**
   * 시설 코드로 시설 정보 조회
   * 사용자 인터페이스용 (URL에서 코드를 받아 내부적으로 ID로 변환)
   */
  getPoolByCode: async (poolCode: string): Promise<ApiResponse<Pool>> => {
    try {
      console.log(`[poolApi] 시설 코드(${poolCode})로 조회 시작`);
      
      // 1. 코드를 ID로 변환
      const poolIdResult = await getPoolIdByCode(poolCode);
      if (poolIdResult === null) {
        return {
          success: false,
          data: null,
          message: `시설 코드 ${poolCode}에 해당하는 ID를 찾기 불가`,
          status: 404
        };
      }
      
      const poolId = poolIdResult;
      console.log(`[poolApi] 코드(${poolCode})에 대한 ID(${poolId}) 확인됨`);
      
      // 2. ID로 API 호출
      return await poolApi.getPoolById(String(poolId));
    } catch (error) {
      console.error(`[poolApi] 시설 코드(${poolCode}) 조회 오류:`, error);
      return handleApiError<Pool>(error);
    }
  },
  
  /**
   * 시설 정보 업데이트
   * 코드를 받아 내부적으로 ID로 변환하여 처리
   */
  updatePool: async (poolCode: string, poolData: Partial<PoolUpdate>): Promise<ApiResponse<Pool>> => {
    try {
      console.log(`[poolApi] 시설 코드(${poolCode}) 정보 업데이트 시작`);
      
      // 1. 코드를 ID로 변환
      const poolIdResult = await getPoolIdByCode(poolCode);
      if (poolIdResult === null) {
        return {
          success: false,
          data: null,
          message: `시설 코드 ${poolCode}에 해당하는 ID를 찾기 불가`,
          status: 404
        };
      }
      
      const poolId = poolIdResult;
      console.log(`[poolApi] 코드(${poolCode})에 대한 ID(${poolId}) 확인됨`);
      
      // 2. ID로 API 호출
      const apiUrl = `/api/pools/${poolId}`;
      console.log(`[poolApi] PATCH ${apiUrl} 요청`, poolData);
      
      // 3. PATCH 메서드로 데이터 업데이트
      const response = await axios.patch(`${API_URL}${apiUrl}`, poolData);
      console.log('[poolApi] 시설 정보 업데이트 응답:', response);
      
      // 4. 업데이트 성공 시 캐시 초기화
      if (response.status >= 200 && response.status < 300) {
        clearPoolCache(poolCode, String(poolId));
      }
      
      return handleApiResponse<Pool>(response);
    } catch (error) {
      console.error(`[poolApi] 시설 코드(${poolCode}) 정보 업데이트 오류:`, error);
      return handleApiError<Pool>(error);
    }
  },
  
  /**
   * 새 시설 생성
   */
  createPool: async (poolData: Omit<Pool, 'id'>): Promise<ApiResponse<Pool>> => {
    try {
      console.log('[poolApi] 새 시설 생성 요청', poolData);
      const response = await axios.post(`${API_URL}/api/pools`, poolData);
      console.log('[poolApi] 시설 생성 응답:', response);
      
      // 생성된 시설이 있으면 캐시 초기화
      if (response.status >= 200 && response.status < 300 && response.data && response.data.id && response.data.code) {
        clearPoolCache(response.data.code, response.data.id);
      }
      
      return handleApiResponse<Pool>(response);
    } catch (error) {
      console.error('[poolApi] 시설 생성 오류:', error);
      return handleApiError<Pool>(error);
    }
  },
  
  /**
   * 시설 삭제
   * 코드를 받아 내부적으로 ID로 변환하여 처리
   */
  deletePool: async (poolCode: string): Promise<ApiResponse<void>> => {
    try {
      console.log(`[poolApi] 시설 코드(${poolCode}) 삭제 시작`);
      
      // 1. 코드를 ID로 변환
      const poolIdResult = await getPoolIdByCode(poolCode);
      if (poolIdResult === null) {
        return {
          success: false,
          data: null,
          message: `시설 코드 ${poolCode}에 해당하는 ID를 찾기 불가`,
          status: 404
        };
      }
      
      const poolId = poolIdResult;
      console.log(`[poolApi] 코드(${poolCode})에 대한 ID(${poolId}) 확인됨`);
      
      // 2. ID로 API 호출
      const response = await axios.delete(`${API_URL}/api/pools/${poolId}`);
      console.log('[poolApi] 시설 삭제 응답:', response);
      
      // 3. 삭제 성공 시 캐시 초기화
      if (response.status >= 200 && response.status < 300) {
        clearPoolCache(poolCode, String(poolId));
      }
      
      return handleApiResponse<void>(response);
    } catch (error) {
      console.error(`[poolApi] 시설 코드(${poolCode}) 삭제 오류:`, error);
      return handleApiError<void>(error);
    }
  },
  
  /**
   * 시설 설정 정보 조회
   * poolCode 또는 poolId를 받아 처리
   * poolId(숫자)를 직접 받거나 poolCode(문자열)를 받아 ID로 변환
   */
  getPoolSettings: async (poolCodeOrId: string | number): Promise<ApiResponse<PoolSettings>> => {
    try {
      // 1. 입력 값이 숫자인지 문자열인지 확인
      const isId = typeof poolCodeOrId === 'number' || !isNaN(Number(poolCodeOrId));
      console.log(`[poolApi] 시설 설정 정보 조회 시작 - 입력 값: ${poolCodeOrId}, 타입: ${typeof poolCodeOrId}, ID로 직접 사용: ${isId}`);
      
      let poolId: number;
      
      if (isId) {
        // 숫자(ID) 직접 사용
        poolId = Number(poolCodeOrId);
        console.log(`[poolApi] ID로 직접 사용: ${poolId}`);
      } else {
        // 문자열(코드)를 ID로 변환
        const poolCodeStr = String(poolCodeOrId);
        const poolIdResult = await getPoolIdByCode(poolCodeStr);
        if (poolIdResult === null) {
          return {
            success: false,
            data: null,
            message: `시설 코드 ${poolCodeStr}에 해당하는 ID를 찾기 불가`,
            status: 404
          };
        }
        poolId = poolIdResult;
        console.log(`[poolApi] 코드(${poolCodeStr})에 대한 ID(${poolId}) 확인됨`);
      }
      
      // 2. ID로 API 호출
      console.log(`[poolApi] 시설 설정 API 호출 URL: ${API_URL}/api/pools/${poolId}/settings`);
      const response = await axios.get(`${API_URL}/api/pools/${poolId}/settings`);
      console.log('[poolApi] 시설 설정 정보 응답:', response.data);
      
      // 3. 응답 데이터 처리 및 변환
      if (response.status === 200) {
        // 응답 데이터가 있으면 처리
        let settingsData: any = null;
        
        // 응답 구조에 따른 처리
        if (response.data && response.data.success === true && response.data.data) {
          // { success: true, data: {...}, message: "..." } 형식
          console.log('[poolApi] API 응답이 성공 객체를 포함하고 있습니다.');
          settingsData = response.data.data;
        } else if (response.data && (response.data.id || response.data.pool_id)) {
          // 직접 설정 객체가 반환된 경우
          console.log('[poolApi] API 응답이 직접 설정 객체를 포함하고 있습니다.');
          settingsData = response.data;
        }
        
        if (settingsData) {
          // 응답 데이터를 PoolSettings 인터페이스 형식으로 변환
          console.log('[poolApi] 원본 응답 데이터:', settingsData);
          
          // PoolSettings 형식 확인 및 변환
          const isCompletePoolSettings = 
            settingsData.water_quality_thresholds && 
            settingsData.safety_thresholds && 
            settingsData.notification_settings && 
            settingsData.auto_control && 
            settingsData.schedule;
          
          if (isCompletePoolSettings) {
            // 이미 올바른 형식인 경우
            console.log('[poolApi] 응답 데이터가 이미 올바른 PoolSettings 형식입니다.');
            return {
              success: true,
              data: settingsData as PoolSettings,
              message: response.data.message || '시설 설정 정보 수신',
              status: response.status
            };
          } else {
            // 다른 형식인 경우 변환 필요
            console.log('[poolApi] 응답 데이터를 PoolSettings 형식으로 변환합니다.');
            
            // 변환된 형식 생성
            const convertedSettings: any = {
              id: settingsData.id || 1,
              pool_id: poolId,
              created_at: settingsData.created_at || new Date().toISOString(),
              updated_at: settingsData.updated_at || new Date().toISOString(),
              
              // 수질 기준
              water_quality_thresholds: {
                ph: { 
                  min: settingsData.ph_min || 7.2, 
                  max: settingsData.ph_max || 7.8 
                },
                chlorine: { 
                  min: settingsData.chlorine_min || 0.5, 
                  max: settingsData.chlorine_max || 2.0 
                },
                temperature: { 
                  min: settingsData.temperature_min || 25, 
                  max: settingsData.temperature_max || 28 
                },
                turbidity: { 
                  min: settingsData.turbidity_min || 0, 
                  max: settingsData.turbidity_max || 1.0 
                }
              },
              
              // 안전 기준
              safety_thresholds: {
                max_occupancy: settingsData.max_capacity_warning || 100,
                lifeguard_count: settingsData.lifeguard_count || 2,
                max_depth: settingsData.max_depth || 2.0,
                emergency_equipment_count: settingsData.emergency_equipment_count || 5
              },
              
              // 알림 설정
              notification_settings: {
                email_alerts: settingsData.email_alerts || true,
                sms_alerts: settingsData.sms_alerts || true,
                app_alerts: settingsData.app_alerts || true,
                webhook_alerts: settingsData.webhook_alerts || false,
                alert_frequency: settingsData.alert_frequency || "immediate"
              },
              
              // 자동 제어 설정
              auto_control: {
                enabled: settingsData.auto_alert || true,
                temperature_control: settingsData.temperature_control || true,
                chlorine_control: settingsData.chlorine_control || true,
                ph_control: settingsData.ph_control || true
              },
              
              // 운영 일정
              schedule: {
                opening_time: (settingsData.operating_hours || "09:00-18:00").split('-')[0] || "06:00",
                closing_time: (settingsData.operating_hours || "09:00-18:00").split('-')[1] || "22:00",
                maintenance_day: settingsData.maintenance_day || "monday",
                maintenance_time: settingsData.maintenance_time || "22:00-06:00"
              }
            };
            
            console.log('[poolApi] 변환된 PoolSettings 형식:', convertedSettings);
            
            return {
              success: true,
              data: convertedSettings as PoolSettings,
              message: response.data.message || '시설 설정 정보 수신',
              status: response.status
            };
          }
        }
      }
      
      // 위의 조건에 해당하지 않는 경우 기본 처리
      return handleApiResponse<PoolSettings>(response);
    } catch (error) {
      console.error(`[poolApi] 시설 설정 정보 조회 오류:`, error);
      
      // API 호출 실패 시 더미 데이터 반환
      console.log('[poolApi] 더미 설정 데이터 반환');
      
      // poolId가 없으면 임의로 1 사용
      let poolId = 1;
      if (typeof poolCodeOrId === 'number') {
        poolId = poolCodeOrId;
      } else if (!isNaN(Number(poolCodeOrId))) {
        poolId = Number(poolCodeOrId);
      }
      
      const dummyPoolSettings: any = {
        id: 1,
        pool_id: poolId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        water_quality_thresholds: {
          ph: { min: 7.2, max: 7.8 },
          chlorine: { min: 0.5, max: 2.0 },
          temperature: { min: 25, max: 28 },
          turbidity: { min: 0, max: 1.0 }
        },
        safety_thresholds: {
          max_occupancy: 100, 
          lifeguard_count: 2,
          max_depth: 2.0,
          emergency_equipment_count: 5
        },
        notification_settings: {
          email_alerts: true,
          sms_alerts: true,
          app_alerts: true,
          webhook_alerts: false,
          alert_frequency: "immediate"
        },
        auto_control: {
          enabled: true,
          temperature_control: true,
          chlorine_control: true,
          ph_control: true
        },
        schedule: {
          opening_time: "06:00",
          closing_time: "22:00",
          maintenance_day: "monday",
          maintenance_time: "22:00-06:00"
        }
      };
      
      return {
        success: true,
        data: dummyPoolSettings,
        message: '[poolApi] 더미 시설 설정 데이터가 제공됨',
        status: 200
      };
    }
  },
  
  /**
   * 시설 설정 정보 업데이트
   * poolCode 또는 poolId를 받아 처리
   * poolId(숫자)를 직접 받거나 poolCode(문자열)를 받아 ID로 변환
   */
  updatePoolSettings: async (poolCodeOrId: string | number, settings: PoolSettings): Promise<ApiResponse<PoolSettings>> => {
    try {
      // 1. 입력 값이 숫자인지 문자열인지 확인
      const isId = typeof poolCodeOrId === 'number' || !isNaN(Number(poolCodeOrId));
      console.log(`[poolApi] 시설 설정 정보 업데이트 시작 - 입력 값: ${poolCodeOrId}, 타입: ${typeof poolCodeOrId}, ID로 직접 사용: ${isId}`);
      
      let poolId: number;
      
      if (isId) {
        // 숫자(ID) 직접 사용
        poolId = Number(poolCodeOrId);
        console.log(`[poolApi] ID로 직접 사용: ${poolId}`);
      } else {
        // 문자열(코드)를 ID로 변환
        const poolCodeStr = String(poolCodeOrId);
        const poolIdResult = await getPoolIdByCode(poolCodeStr);
        if (poolIdResult === null) {
          return {
            success: false,
            data: null,
            message: `시설 코드 ${poolCodeStr}에 해당하는 ID를 찾기 불가`,
            status: 404
          };
        }
        poolId = poolIdResult;
        console.log(`[poolApi] 코드(${poolCodeStr})에 대한 ID(${poolId}) 확인됨`);
      }
      
      // 2. ID로 API 호출
      console.log(`[poolApi] 시설 설정 업데이트 API 호출 URL: ${API_URL}/api/pools/${poolId}/settings`);
      const response = await axios.put(`${API_URL}/api/pools/${poolId}/settings`, settings);
      console.log('[poolApi] 시설 설정 업데이트 응답:', response);
      
      return handleApiResponse<PoolSettings>(response);
    } catch (error) {
      console.error(`[poolApi] 시설 설정 정보 업데이트 오류:`, error);
      
      // API 호출 실패 시 가짜 성공 응답 반환
      console.log('[poolApi] 더미 설정 업데이트 성공 반환');
      
      // 입력 받은 설정을 그대로 반환하고 현재 시간으로 updated_at 업데이트
      const updatedSettings = {
        ...settings,
        updated_at: new Date().toISOString()
      };
      
      return {
        success: true,
        data: updatedSettings,
        message: '[poolApi] 더미 시설 설정이 업데이트 완료',
        status: 200
      };
    }
  }
}; 